import { useEffect, useState } from "react";
import { Download, Loader2, Smartphone, Wifi, RefreshCw } from "lucide-react";
import { apkInfo, APK_URL, downloadOfflineFile } from "@/lib/api";
import { toast } from "sonner";

export default function GetApp() {
  const [downloading, setDownloading] = useState(false);
  const [apk, setApk] = useState({ available: false });

  useEffect(() => {
    apkInfo().then(setApk).catch(() => {});
  }, []);

  const handleOffline = async () => {
    setDownloading(true);
    try {
      await downloadOfflineFile();
      toast.success("Gem Key Offline downloaded — open it in any browser anywhere.");
    } catch {
      toast.error("Download failed — please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <section className="space-y-10 max-w-5xl mx-auto">
      <header className="space-y-3">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] muted">// Get The App</div>
        <h1 className="font-display text-3xl md:text-5xl tracking-tight">
          Take Gem Key <span className="gold italic">anywhere</span>
        </h1>
        <p className="muted text-sm max-w-3xl leading-relaxed">
          Three ways to carry the whole dictionary in your pocket — a single offline file,
          the Android app, or install straight from the browser.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* OFFLINE HTML */}
        <div className="panel p-6 border-[rgba(212,175,55,0.4)]" data-testid="getapp-offline-card">
          <div className="flex items-center gap-2 mb-3">
            <Wifi className="gold shrink-0" size={16} />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] gold">
              Offline Edition — one file
            </span>
          </div>
          <div className="text-base md:text-xl font-semibold mb-2 leading-snug">
            The whole dictionary, <span className="gold">no internet needed</span>.
          </div>
          <p className="muted text-sm leading-relaxed mb-4">
            One HTML file (~570&nbsp;KB) with every phrase, all 20 ciphers, the Dark / Neutral / Light
            search, calculator and date tools. Save it to your phone or a USB stick — opens in any
            browser, works forever.
          </p>
          <button
            onClick={handleOffline}
            disabled={downloading}
            data-testid="getapp-download-offline-btn"
            className="btn-primary px-5 py-3 text-xs flex items-center justify-center gap-2 disabled:opacity-50 w-full md:w-auto"
          >
            {downloading ? <Loader2 className="animate-spin" size={14} /> : <Download size={14} />}
            {downloading ? "Building…" : "Download Offline HTML"}
          </button>
          <p className="muted text-[11px] font-mono mt-3 leading-relaxed">
            Tip: re-download after new phrases are added — the file is built fresh from the live
            dictionary every time.
          </p>
        </div>

        {/* ANDROID APK */}
        <div className="panel p-6" data-testid="getapp-apk-card">
          <div className="flex items-center gap-2 mb-3">
            <Smartphone className="gold shrink-0" size={16} />
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] gold">
              Android App (.apk)
            </span>
          </div>
          <div className="text-base md:text-xl font-semibold mb-2 leading-snug">
            Install Gem Key as a <span className="gold">real Android app</span>.
          </div>
          <p className="muted text-sm leading-relaxed mb-4">
            The APK wraps the live site — home-screen icon, full screen, no browser bars.
            Needs internet for fresh data; pairs perfectly with the Offline Edition above.
          </p>
          {apk.available ? (
            <a
              href={APK_URL}
              data-testid="getapp-download-apk-btn"
              className="btn-primary px-5 py-3 text-xs flex items-center justify-center gap-2 w-full md:w-auto"
            >
              <Download size={14} /> Download APK{apk.size_mb ? ` (${apk.size_mb} MB)` : ""}
            </a>
          ) : (
            <div
              className="border border-white/10 px-4 py-3 text-xs font-mono muted"
              data-testid="getapp-apk-unavailable"
            >
              APK not uploaded yet — coming soon.
            </div>
          )}
          <p className="muted text-[11px] font-mono mt-3 leading-relaxed">
            Android will ask to allow installs from unknown sources — that's normal for
            apps outside the Play Store.
          </p>
        </div>
      </div>

      {/* HOW UPDATES WORK */}
      <div className="panel panel-narrative p-6 flex gap-4 items-start" data-testid="getapp-updates-note">
        <RefreshCw className="gold shrink-0 mt-1" size={18} />
        <div className="text-sm leading-relaxed">
          <div className="font-display text-lg mb-1">How updates reach you</div>
          <ul className="muted font-mono text-xs space-y-1.5 leading-relaxed list-none">
            <li>• <span className="text-white">Android app</span> — updates itself automatically. It loads the live site, so new phrases and features appear next time you open it. No re-install needed.</li>
            <li>• <span className="text-white">Offline HTML</span> — a frozen snapshot. Re-download it here whenever you want the latest dictionary.</li>
            <li>• <span className="text-white">Browser install</span> — on Android Chrome: menu (⋮) → "Add to Home screen". Same self-updating behaviour as the APK.</li>
          </ul>
        </div>
      </div>
    </section>
  );
}
